import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  AUTH_HOST_ERROR_PRESENTER,
  AUTH_HOST_LOGGER,
  AuthModule,
} from '@core-features/auth';
import { AUTH_SERVICE, USER_SERVICE } from '@core-features/core';
import {
  COPILOT_CHAT_DEFAULT_LABELS,
  COPILOT_CHAT_LABELS,
  COPILOT_KIT_CONFIG,
  CopilotChatLabels,
  CopilotKitConfig,
} from '@copilotkit/angular';
import { environment } from '../environments/environment';
import { PalchatAuthErrorPresenter, PalchatAuthLogger } from './auth-adapters';
import { CHAT_HUB_AGENT_ID, createChatHubAgent } from './chat-hub-agent';
import { PalchatUserService } from './palchat-user.service';

export { CHAT_HUB_AGENT_ID };

/** Chat labels; the greeting reflects the signed-in user. */
export function providePalchatChatLabels(): Provider {
  return {
    provide: COPILOT_CHAT_LABELS,
    useFactory: (): CopilotChatLabels => {
      const authService = inject(AUTH_SERVICE);

      return {
        ...COPILOT_CHAT_DEFAULT_LABELS,
        chatDisclaimerText:
          'PalChat is an AI assistant and can make mistakes. Please verify important information.',
        get welcomeMessageText(): string {
          const firstname = authService.$userInfo()?.firstname;

          const greeting = firstname ? `Hey ${firstname}` : 'Hey there';

          return `${greeting}, I'm PalChat, PALFINGER's AI assistant. How can I help you today?`;
        },
      };
    },
  };
}

export function createAppConfig(): ApplicationConfig {
  return {
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true }),
      providePalchatChatLabels(),
      provideHttpClient(withInterceptorsFromDi()),
      { provide: USER_SERVICE, useClass: PalchatUserService },
      // AuthHostService injects Router and navigates on sign-out. The widget
      // itself is not routed, so a single catch-all keeps those navigations
      // from failing without adding routed views.
      provideRouter([{ path: '**', children: [] }]),
      importProvidersFrom(
        AuthModule.forHost({
          config: {
            securityTokenService: environment.securityTokenService,
            oidcClientId: environment.oidcClientId,
            oidcScope: environment.oidcScope,
            postLogoutRedirectUri: window.location.origin,
          },
          logger: { provide: AUTH_HOST_LOGGER, useClass: PalchatAuthLogger },
          presenter: {
            provide: AUTH_HOST_ERROR_PRESENTER,
            useClass: PalchatAuthErrorPresenter,
          },
          // Nothing here reaches a PALDESK API through HttpClient: AG-UI talks to
          // Chat Hub over fetch and user info goes through OAuthService, so the
          // interceptor has nothing to attach a token to.
          interceptorConfig: { whitelistedDomains: [] },
        }),
      ),
      // Replaces `provideCopilotKit`, which takes a literal config and so
      // cannot reach the auth service. Only the license/header merging is
      // skipped, and no license key is configured.
      {
        provide: COPILOT_KIT_CONFIG,
        useFactory: (): CopilotKitConfig => ({
          enableInspector: false,
          selfManagedAgents: {
            [CHAT_HUB_AGENT_ID]: createChatHubAgent(
              environment.chatHubUrl,
              inject(AUTH_SERVICE),
            ),
          },
        }),
      },
    ],
  };
}
